/**
 * Setup wizard state machine (§9).
 *
 * Persisted to localStorage so closing the modal and reopening returns to the
 * same step with the same answers.
 */

import type { PageTypeRule } from "./fixtures/attribution-demo";
import { DEFAULT_PAGE_TYPE_RULES, KEY_EVENTS } from "./fixtures/attribution-demo";
import type { OAuthErrorCode } from "./fixtures/oauth-demo";
import type { DevSyncState } from "./fixtures/sync-demo";

export type ChannelOutcome = "MISSING" | "BROKEN" | "OK";
/** Which setup route the customer picked on step 1. */
export type SetupPath = "ga4" | "revenue_stack";
export type LookbackDays = 30 | 90 | 180;
export const WIZARD_STEP_COUNT = 5;

export type SetupState = {
    /** 1-based index into the step rows for the chosen path. */
    step: number;
    /** null until the customer chooses on step 1. */
    setupPath: SetupPath | null;
    connected: boolean;
    /** Attribution can read from several GA4 properties at once. */
    propertyIds: string[];
    /** Email of the connected Google account, shown in the success row. */
    googleAccountEmail: string | null;
    /** Result of the channel check. null until step 2 has run. */
    channelOutcome: ChannelOutcome | null;
    /** True once the user rechecks and it comes back OK. */
    channelFixed: boolean;
    /**
     * An OK channel check auto-advances the wizard. This latches so that pressing
     * Back from step 3 doesn't immediately bounce the user forward again.
     */
    channelAutoAdvanced: boolean;
    /** True if the user pressed "Skip for now" — drives the report warning banner (§6). */
    channelSkipped: boolean;
    /**
     * Step ids the user has advanced past, in completion order. Persisted so a reload
     * restores the stepper's checkmarks and resumes at the first incomplete step, rather
     * than only recording the config at the final finishSetup. Typed as string (not StepId)
     * to avoid a circular import with wizardSteps.
     */
    completedSteps: string[];
    selectedEvents: string[];
    currency: string;
    /** Per-event value, keyed by event name. */
    eventValues: Record<string, number>;
    /**
     * Whether GA4 reports native revenue for the selected conversions (from the revenue probe).
     * Persisted so the backend picks the native-revenue path when true and the derived
     * key-events × deal-value path when false, instead of having to guess.
     */
    hasGa4Revenue: boolean;
    lookbackDays: LookbackDays;
    rules: PageTypeRule[];
    /** Dev-only previews of the mocked backend responses (§4). */
    devChannelOutcome: ChannelOutcome;
    devNoKeyEvents: boolean;
    devGa4Revenue: boolean;
    /** MOCK: forces a specific OAuth failure path in the simulated consent flow. */
    devOauthError: OAuthErrorCode | null;
    /** MOCK: forces a sync health state on the report. */
    devSyncState: DevSyncState;
};

export const CURRENCIES = [
    { code: "USD", symbol: "$" },
    { code: "EUR", symbol: "€" },
    { code: "GBP", symbol: "£" },
    { code: "INR", symbol: "₹" }
];

export function currencySymbol(code: string): string {
    return CURRENCIES.find((c) => c.code === code)?.symbol ?? "$";
}

export function initialSetupState(): SetupState {
    return {
        step: 1,
        setupPath: null,
        connected: false,
        propertyIds: [],
        googleAccountEmail: null,
        channelOutcome: null,
        channelFixed: false,
        channelAutoAdvanced: false,
        channelSkipped: false,
        completedSteps: [],
        selectedEvents: KEY_EVENTS.filter((e) => e.defaultSelected).map((e) => e.name),
        currency: "USD",
        eventValues: Object.fromEntries(KEY_EVENTS.map((e) => [e.name, e.defaultValue])),
        hasGa4Revenue: false,
        lookbackDays: 90,
        rules: DEFAULT_PAGE_TYPE_RULES.map((r) => ({ ...r, patterns: [...r.patterns] })),
        // §4 step 2 defaults to BROKEN — it is the most instructive outcome.
        devChannelOutcome: "BROKEN",
        devNoKeyEvents: false,
        devGa4Revenue: false,
        devOauthError: null,
        devSyncState: "HEALTHY"
    };
}

/**
 * The subset of SetupState that is persisted to the backend (§ DB save). Dev toggles
 * (`dev*`) and transient wizard-navigation fields (`step`, `channelAutoAdvanced`) are the
 * wizard's own runtime concerns and are deliberately excluded — only the actual attribution
 * configuration is stored.
 */
export type PersistedSetup = {
    /** Which route was chosen — real configuration, so it survives a reload. */
    setupPath: SetupPath | null;
    connected: boolean;
    propertyIds: string[];
    googleAccountEmail: string | null;
    channelOutcome: ChannelOutcome | null;
    channelFixed: boolean;
    channelSkipped: boolean;
    /** Which steps the user has completed, so partial progress survives a reload (§ step-completion). */
    completedSteps: string[];
    selectedEvents: string[];
    currency: string;
    eventValues: Record<string, number>;
    /** Native GA4 revenue present for the selected conversions — picks the backend revenue path. */
    hasGa4Revenue: boolean;
    lookbackDays: LookbackDays;
    rules: PageTypeRule[];
};

/** Strip the runtime/dev fields, leaving only the config we save to the DB. */
export function toPersistedSetup(state: SetupState): PersistedSetup {
    return {
        setupPath: state.setupPath,
        connected: state.connected,
        propertyIds: state.propertyIds,
        googleAccountEmail: state.googleAccountEmail,
        channelOutcome: state.channelOutcome,
        channelFixed: state.channelFixed,
        channelSkipped: state.channelSkipped,
        completedSteps: state.completedSteps,
        selectedEvents: state.selectedEvents,
        currency: state.currency,
        eventValues: state.eventValues,
        hasGa4Revenue: state.hasGa4Revenue,
        lookbackDays: state.lookbackDays,
        rules: state.rules
    };
}

/**
 * Merge a backend-loaded PersistedSetup over the defaults to rebuild a full SetupState. Unknown
 * or missing fields fall back to the initial state, so a stale saved shape can't break the wizard.
 */
export function fromPersistedSetup(persisted: Partial<PersistedSetup> | null | undefined): SetupState {
    const base = initialSetupState();
    if (!persisted) return base;
    return {
        ...base,
        ...persisted,
        // Never resurrect a mid-wizard step from a saved config — a loaded setup is complete.
        step: 1,
        rules: Array.isArray(persisted.rules)
            ? persisted.rules.map((r) => ({ ...r, patterns: [...r.patterns] }))
            : base.rules
    };
}

const STORAGE_KEY = "citedby.revenueAttribution.setup";

export function loadSetupState(): SetupState {
    if (typeof window === "undefined") return initialSetupState();
    try {
        const raw = window.localStorage.getItem(STORAGE_KEY);
        if (!raw) return initialSetupState();
        const parsed = JSON.parse(raw) as Partial<SetupState>;
        // Merge over defaults so a stale shape from an older build can't break the wizard.
        return { ...initialSetupState(), ...parsed };
    } catch {
        return initialSetupState();
    }
}

export function saveSetupState(state: SetupState): void {
    if (typeof window === "undefined") return;
    try {
        window.localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
    } catch {
        /* storage unavailable — the wizard still works, it just won't resume */
    }
}

export function clearSetupState(): void {
    if (typeof window === "undefined") return;
    try {
        window.localStorage.removeItem(STORAGE_KEY);
    } catch {
        /* no-op */
    }
}

/** §9 — every async operation is a mocked promise with a realistic delay. */
export function mockDelay(ms: number): Promise<void> {
    return new Promise((resolve) => setTimeout(resolve, ms));
}
